import React, { useState } from 'react'
import { Card, CardBody, Chip, IconButton, Typography } from '@material-tailwind/react'
import { PiTrash } from 'react-icons/pi'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import RemoveExperienceDialog from './RemoveExperienceDialog'
import { formatDateString } from '../utils/helpers/dates.helpers'
import { useDarkMode } from '../contexts/DarkModeContext'
import { AgamiExperience } from '../types/supabase'

interface ExperiencePreviewCardProps {
  experience: AgamiExperience
  refetchExpList: () => void
  /** Renders no action controls at all — not disabled ones (AC-7 of story 02.1). */
  readOnly?: boolean
  /** Already-translated role label; the conversion lives in sharing.helpers. */
  roleLabel?: string
  /** Click destination; defaults to the owned-experience dashboard. */
  to?: string
}

const ExperiencePreviewCard: React.FC<ExperiencePreviewCardProps> = ({
  experience,
  refetchExpList,
  readOnly = false,
  roleLabel,
  to,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  const { darkMode } = useDarkMode()

  const startDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setOpenConfirmDelete(true)
  }

  return (
    <>
      {!readOnly && (
        <RemoveExperienceDialog
          isOpen={openConfirmDelete}
          setIsOpen={setOpenConfirmDelete}
          experienceId={experience.id}
          experienceName={experience.name}
          refetchExpList={refetchExpList}
        />
      )}

      <Card
        className="w-full h-full cursor-pointer hover:bg-base-gray-500 dark:hover:bg-primary-300"
        onClick={() => navigate(to ?? `/experiences/${experience.id}`)}
        data-testclass="experience-preview-card"
        data-experience-id={experience.id}
      >
        <CardBody>
          <div className="flex space-x-2 justify-between items-start">
            <div className="flex space-x-4 items-center">
              <div className="flex-1">
                <Typography className="font-bold" data-testclass="experience-preview-card-name">
                  {experience.name}
                </Typography>
                {!experience.is_default && (
                  <span className="inline-block mb-2">
                    {experience.is_active ? (
                      <Chip value="active" size="sm" variant={darkMode ? 'filled' : 'ghost'} color="green" />
                    ) : (
                      <Chip
                        value="inactive"
                        size="sm"
                        variant={darkMode ? 'filled' : 'ghost'}
                        color="amber"
                      />
                    )}
                  </span>
                )}
                {roleLabel && (
                  <span className="inline-block mb-2 ml-1">
                    <Chip
                      value={roleLabel}
                      size="sm"
                      variant={darkMode ? 'filled' : 'ghost'}
                      color="blue-gray"
                      data-testclass="experience-preview-card-role-chip"
                    />
                  </span>
                )}
                <Typography variant="small">
                  {t('dashboard.created')}{' '}
                  <strong className="text-black dark:text-base-gray-400">
                    {formatDateString(experience?.created_at)}
                  </strong>
                </Typography>
              </div>
            </div>
            {!readOnly && !experience.is_default && (
              <IconButton
                variant="text"
                size="sm"
                color="yellow"
                onClick={startDelete}
                data-testclass="experience-preview-card-delete-btn"
              >
                <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
              </IconButton>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default ExperiencePreviewCard
